// C:\Users\phill\paperplay\frontend\src\features\accountsettings\store\settingsSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import type { PersistConfig } from 'redux-persist';
import type { PersistMigrate, PersistedState } from 'redux-persist/es/types';

import type { Nullable } from '../../../types/commonTypes';

/** Local UI settings (client‑side, persisted) */
export interface SettingsState {
  theme: 'light' | 'dark' | 'system';
  notificationsEnabled: boolean;
  language: string;
  autoSaveInterval: Nullable<number>;
  error: Nullable<string>;
  lastUpdated: Nullable<string>;
  experimentalFeaturesEnabled: boolean;
  sidebarCollapsed: boolean;
  accessibilityFeaturesEnabled: boolean;
}

export const initialSettingsState: SettingsState = {
  theme: 'system',
  notificationsEnabled: true,
  language: 'en',
  autoSaveInterval: 15,
  error: null,
  lastUpdated: null,
  experimentalFeaturesEnabled: false,
  sidebarCollapsed: false,
  accessibilityFeaturesEnabled: false,
};

const slice = createSlice({
  name: 'settings',
  initialState: initialSettingsState,
  reducers: {
    /** Cycle light → dark → system → light */
    toggleTheme(state) {
      const t = state.theme;
      state.theme = t === 'light' ? 'dark' : t === 'dark' ? 'system' : 'light';
      state.lastUpdated = new Date().toISOString();
    },
    /** Set a specific theme */
    setTheme(state, action: PayloadAction<'light' | 'dark' | 'system'>) {
      state.theme = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    setNotificationsEnabled(state, action: PayloadAction<boolean>) {
      state.notificationsEnabled = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    setLanguage(state, action: PayloadAction<string>) {
      state.language = (action.payload || '').trim() || 'en';
      state.lastUpdated = new Date().toISOString();
    },
    setAutoSaveInterval(state, action: PayloadAction<Nullable<number>>) {
      state.autoSaveInterval = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    toggleExperimentalFeatures(state) {
      state.experimentalFeaturesEnabled = !state.experimentalFeaturesEnabled;
      state.lastUpdated = new Date().toISOString();
    },
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      state.lastUpdated = new Date().toISOString();
    },
    setAccessibilityFeaturesEnabled(state, action: PayloadAction<boolean>) {
      state.accessibilityFeaturesEnabled = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    /** Set an error string (used by thunks) */
    setSettingsError(state, action: PayloadAction<string | null>) {
      state.error = action.payload ?? null;
    },
    clearSettingsError(state) {
      state.error = null;
    },
    /** Apply a partial patch (pure, no batching API needed in reducers) */
    batchUpdateSettings(state, action: PayloadAction<Partial<SettingsState>>) {
      Object.assign(state, action.payload);
      state.lastUpdated = new Date().toISOString();
    },
  },
});

export const {
  toggleTheme,
  setTheme,
  setNotificationsEnabled,
  setLanguage,
  setAutoSaveInterval,
  toggleExperimentalFeatures,
  toggleSidebar,
  setAccessibilityFeaturesEnabled,
  setSettingsError,
  clearSettingsError,
  batchUpdateSettings,
} = slice.actions;

/** ───────── Migration helpers ───────── */
const PERSIST_VERSION = 2;

const toBool = (v: unknown, fallback: boolean) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  return fallback;
};

const toNumOrNull = (v: unknown, fallback: Nullable<number>) => {
  if (v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const isValidTheme = (t: unknown): t is SettingsState['theme'] =>
  t === 'light' || t === 'dark' || t === 'system';

/**
 * IMPORTANT:
 *  - Signature matches PersistMigrate (state, currentVersion) => Promise<PersistedState>
 *  - We DO NOT return `_persist` at runtime; we just cast for TS compatibility.
 */
const migrateSettings: PersistMigrate = async (state, _currentVersion) => {
  // First install (no persisted state yet)
  if (!state) {
    const fresh = { ...initialSettingsState };
    // Type-only cast; runtime object contains no `_persist`
    return fresh as unknown as PersistedState;
  }

  // Strip redux-persist bookkeeping if present
  const { _persist: _ignored, ...raw } = (state as any) ?? {};

  const next: SettingsState = {
    theme: isValidTheme(raw.theme) ? raw.theme : 'system',
    notificationsEnabled: toBool(
      raw.notificationsEnabled,
      initialSettingsState.notificationsEnabled
    ),
    language:
      typeof raw.language === 'string' && raw.language.trim().length > 0
        ? raw.language.trim()
        : initialSettingsState.language,
    autoSaveInterval: toNumOrNull(raw.autoSaveInterval, initialSettingsState.autoSaveInterval),
    error: null,         // never persist transient error
    lastUpdated: null,   // reset timestamp on migration
    experimentalFeaturesEnabled: toBool(
      raw.experimentalFeaturesEnabled,
      initialSettingsState.experimentalFeaturesEnabled
    ),
    sidebarCollapsed: toBool(raw.sidebarCollapsed, initialSettingsState.sidebarCollapsed),
    accessibilityFeaturesEnabled: toBool(
      raw.accessibilityFeaturesEnabled,
      initialSettingsState.accessibilityFeaturesEnabled
    ),
  };

  // Return only the slice state; TS cast satisfies PersistMigrate
  return next as unknown as PersistedState;
};

/** Persist only user‑meaningful prefs; skip transient fields */
const persistConfig: PersistConfig<SettingsState> = {
  key: 'settings',
  storage,
  version: PERSIST_VERSION,
  whitelist: [
    'theme',
    'language',
    'notificationsEnabled',
    'autoSaveInterval',
    'experimentalFeaturesEnabled',
    'sidebarCollapsed',
    'accessibilityFeaturesEnabled',
  ],
  migrate: migrateSettings,
};

export const settingsReducer = persistReducer<SettingsState>(persistConfig, slice.reducer);
export default settingsReducer;
